import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import type { TypKontroly } from '../../../api/JsdhNotifikaceService/generated/models/TypKontroly';
import {
    useDeleteTypKontrolyMutation,
    useNastavTypAktivniMutation,
} from '../../../api/JsdhNotifikaceService/service/KontrolyMutations';
import { QueryError } from '../../../api/QueryError';

/**
 * Smazani typu kontroly (spec 4.6, 6.1 bod 5). Typ s historii server odmitne 409 MA_HISTORII,
 * dialog pak ukaze jeho hlasku a nabidne deaktivaci pres PATCH /aktivni (vratna, historii zachova).
 */
const SmazatTypKontrolyDialog: React.FC<{ typ: TypKontroly | null; onClose: () => void }> = ({ typ, onClose }) => {
    const deleteMutation = useDeleteTypKontrolyMutation();
    const aktivniMutation = useNastavTypAktivniMutation();
    const [konflikt, setKonflikt] = useState<string | null>(null);

    useEffect(() => {
        setKonflikt(null);
    }, [typ?.id]);

    const nahrazuje = typ?.nahrazuje ?? [];

    const smazat = () => {
        if (!typ) return;
        deleteMutation.mutate(typ.id, {
            onSuccess: () => onClose(),
            onError: (e) => {
                if (e instanceof QueryError && e.statusCode === 409 && e.code === 'MA_HISTORII') {
                    setKonflikt(e.message);
                }
            },
        });
    };

    const deaktivovat = () => {
        if (!typ) return;
        aktivniMutation.mutate({ id: typ.id, aktivni: false }, { onSuccess: () => onClose() });
    };

    return (
        <Dialog open={!!typ} onClose={onClose}>
            <DialogTitle>Smazat typ kontroly</DialogTitle>
            <DialogContent>
                {konflikt ? (
                    <Alert severity="error" sx={{ mt: 1 }}>{konflikt}</Alert>
                ) : (
                    <>
                        <Typography sx={{ mt: 1 }}>
                            Opravdu smazat typ kontroly „{typ?.nazev}“? Smazat lze jen kontrolu, která nemá žádné záznamy v historii.
                        </Typography>
                        {nahrazuje.length > 0 && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                {`Tato kontrola nahrazuje: ${nahrazuje.map((n) => n.typ.nazev).join(', ')}. Po smazání se nahrazené kontroly dotčeným hasičům znovu zobrazí.`}
                            </Alert>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Zrušit</Button>
                {konflikt ? (
                    <Button variant="contained" onClick={deaktivovat} disabled={aktivniMutation.isPending}>
                        Deaktivovat
                    </Button>
                ) : (
                    <Button color="error" variant="contained" onClick={smazat} disabled={deleteMutation.isPending}>
                        Smazat
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default SmazatTypKontrolyDialog;
